// Mantis #1453 — M001-D029 §6/§7, contract SAPI-D029-004
// (MembershipChangeOutcome, discriminated by outcome). One row per
// submitted change, in submitted order — a per-change refusal never
// invalidates or removes any sibling's row (M001-F0310).
//
// Ruling 3 (lead, Mantis #1453): DOOR_WRITE_BLOCKED is a CONSEQUENCE of an
// APPLIED change, never a refusal. This component renders every
// outcome:'applied' entry as a SUCCESS — including one carrying
// doorWriteStatus:DOOR_WRITE_BLOCKED, which additionally shows RealmAuthority's
// own message plus the plain-language consequence, in the RA console's own
// wording ("added to the group, but their card still has to be added on the
// door itself."). It is never rendered as failed and never dropped silently.
//
// M004-F0669 (WebUI #102): an applied outcome names the DIRECTORY change and
// never claims the door. RealmAuthority acks 'applied' once AD has changed —
// before its asynchronous door write — so Core sends doorWriteStatus:null
// (contract 1.1.0: "applied, door state pending; never failed, never an
// assumed OK"). null renders "door access is not confirmed yet" on an amber
// row, never the plain green success; only a real OK says the door update
// was confirmed. A removal also says that door access granted through
// another group is unaffected (RA #1566 door-scoped removal). No row derives
// any "go to the door" affordance from any status.
//
// M004-F0698 / F0699: the editor polls getRealmAuthorityMembershipDoorOutcomes
// for the commandIds of the rows below (useDoorOutcomePoll) and passes the
// settled results in `doorResults`, keyed by commandId. An applied row whose
// ack said null is upgraded ONLY by the entry for ITS OWN commandId (and the
// same person and op): OK -> "RealmAuthority confirmed the door update.";
// DOOR_WRITE_BLOCKED -> the blocked wording plus RA's own detail; a non-empty
// retainedAccess is said plainly beside either. No entry, a null status or
// an entry for another command leaves the row exactly as the ack left it.
// When the poll window ends unresolved the list offers "Check again".
//
// M004-F0630: a 'failed' outcome whose failureReason is RA_OFFLINE is Core's
// ack wait (3 s, deliberately short — Core's, never changed here) expiring
// before RealmAuthority's own AD work finished. On a slow domain controller
// this is common, and the change frequently DID apply. It is therefore
// rendered as "not confirmed — may still have been applied, refresh", with a
// Refresh action that re-fetches the real mirror, and never as RealmAuthority
// having "gone offline before this command could resolve".
import React from 'react';
import { isTimedOutOutcome, matchDoorOutcome } from './normalisers';
import './realmAuthority.css';

export const TIMED_OUT_TEXT = 'RealmAuthority did not confirm this change within Core\u2019s wait. The change may still have been applied — refresh to see the current membership before retrying.';

const personLabel = (objectSid, directoryMap) => {
  const entry = directoryMap?.get(objectSid);
  return entry?.displayName || entry?.upn || objectSid;
};

const doorConsequence = (op) => (op === 'remove'
  ? 'their card still needs to be removed from the door itself.'
  : 'their card still has to be added on the door itself.');

const opVerb = (op) => (op === 'remove' ? 'removed from the group' : 'added to the group');

export const DOOR_PENDING_TEXT = 'Door access is not confirmed yet: RealmAuthority updates the door separately, after this change.';
export const DOOR_OK_TEXT = 'RealmAuthority confirmed the door update.';
export const OTHER_GROUP_TEXT = 'Door access granted through another group is not affected.';

export const DOOR_UNREPORTED_TEXT = 'RealmAuthority has not reported the door result yet.';
export const DOOR_CHECK_FAILED_TEXT = 'Could not check with Core. Try again.';

const REASON_SENTENCE = {
  OTHER_GROUP_GRANT: (door, groups) => (groups.length
    ? `${door}: through another group (${groups.join(', ')}).`
    : `${door}: through another group.`),
  LEGACY_CARD_RECORD: (door) => `${door}: through a legacy card record.`,
  BREAK_GLASS_ALLOWLIST: (door) => `${door}: through the break-glass allowlist.`,
  REMOVAL_WITHHELD: (door) => `${door}: the removal was withheld at this door.`,
};

// One sentence per retained-access entry; closed reasons only, never a
// card reference (none exists on the wire).
export const retainedAccessSentences = (entries) => (entries || []).map((entry) => (
  REASON_SENTENCE[entry.reason](entry.doorLabel, (entry.viaGroups || []).map((group) => group.name))
));

// The settled door result for an applied row — only the entry for the row's
// own commandId (matchDoorOutcome also requires the same person and op) and
// only when it carries a status.
const settledDoorResult = (outcome, doorResults) => {
  if (outcome.doorWriteStatus !== null) return null;
  const entry = doorResults?.get?.(outcome.commandId);
  const match = entry ? matchDoorOutcome(outcome, [entry]) : null;
  return match && match.doorWriteStatus !== null ? match : null;
};

export default function RealmAuthorityMembershipOutcomes({
  outcomes = [], directoryMap, onRefresh, doorResults, doorPoll,
}) {
  if (!outcomes.length) return null;
  const unresolved = outcomes.some((entry) => (
    entry.outcome === 'applied' && entry.doorWriteStatus === null && entry.commandId
    && !settledDoorResult(entry, doorResults)
  ));
  return (
    <>
    <ul className="ra-outcomes" data-testid="ra-membership-outcomes" aria-label="Membership change results">
      {outcomes.map((outcome) => {
        const label = personLabel(outcome.objectSid, directoryMap);
        if (outcome.outcome === 'applied') {
          const door = settledDoorResult(outcome, doorResults);
          const doorStatus = door ? door.doorWriteStatus : outcome.doorWriteStatus;
          const blocked = doorStatus === 'DOOR_WRITE_BLOCKED';
          const confirmed = doorStatus === 'OK';
          const retained = door?.retainedAccess?.length ? retainedAccessSentences(door.retainedAccess) : [];
          const pending = !blocked && !confirmed;
          const doorClass = blocked ? 'ra-outcome--door-blocked' : (pending ? 'ra-outcome--door-pending' : '');
          return (
            <li
              key={outcome.objectSid}
              className={`ra-outcome ra-outcome--applied ${doorClass}`}
              data-testid={`ra-outcome-${outcome.objectSid}`}
              role="status"
            >
              <strong>{label}</strong> was {opVerb(outcome.op)} in the directory.
              {blocked ? (
                <span className="ra-outcome-door-warning" data-testid={`ra-outcome-door-blocked-${outcome.objectSid}`}>
                  {' '}{label} was {opVerb(outcome.op)}, but {doorConsequence(outcome.op)}
                  {' '}({outcome.message})
                  {door?.doorWriteDetail ? (
                    <span data-testid={`ra-outcome-door-detail-${outcome.objectSid}`}>{' '}{door.doorWriteDetail}</span>
                  ) : null}
                </span>
              ) : null}
              {pending ? (
                <span className="ra-outcome-door-pending" data-testid={`ra-outcome-door-pending-${outcome.objectSid}`}>
                  {' '}{DOOR_PENDING_TEXT}
                </span>
              ) : null}
              {confirmed ? (
                <span className="ra-outcome-door-ok" data-testid={`ra-outcome-door-ok-${outcome.objectSid}`}>
                  {' '}{DOOR_OK_TEXT}
                </span>
              ) : null}
              {retained.length ? (
                <span className="ra-outcome-door-retained" data-testid={`ra-outcome-retained-${outcome.objectSid}`}>
                  {' '}{label} still has door access after this change. {retained.join(' ')}
                </span>
              ) : null}
              {!blocked && !retained.length && outcome.op === 'remove' ? (
                <span className="ra-outcome-door-other" data-testid={`ra-outcome-door-other-${outcome.objectSid}`}>
                  {' '}{OTHER_GROUP_TEXT}
                </span>
              ) : null}
            </li>
          );
        }
        if (outcome.outcome === 'refused') {
          return (
            <li
              key={outcome.objectSid}
              className="ra-outcome ra-outcome--refused"
              data-testid={`ra-outcome-${outcome.objectSid}`}
              role="alert"
            >
              <strong>{label}</strong> was not changed: RealmAuthority refused this change
              {' '}(<span data-testid={`ra-outcome-reason-${outcome.objectSid}`}>{outcome.refusalReason}</span>).
              {' '}{outcome.message}
            </li>
          );
        }
        if (isTimedOutOutcome(outcome)) {
          return (
            <li
              key={outcome.objectSid}
              className="ra-outcome ra-outcome--failed ra-outcome--timed-out"
              data-testid={`ra-outcome-${outcome.objectSid}`}
              role="alert"
            >
              <strong>{label}</strong>: <span data-testid={`ra-outcome-timed-out-${outcome.objectSid}`}>{TIMED_OUT_TEXT}</span>
              {' '}(<span data-testid={`ra-outcome-reason-${outcome.objectSid}`}>{outcome.failureReason}</span>)
              {onRefresh ? (
                <>
                  {' '}
                  <button type="button" className="btn btn-outline ra-outcome-refresh" onClick={() => onRefresh()} data-testid={`ra-outcome-refresh-${outcome.objectSid}`}>
                    Refresh
                  </button>
                </>
              ) : null}
            </li>
          );
        }
        // failed — a protocol-level failure outside the five decided refusal
        // reasons (EDITOR-GAP-PROTOCOL-FAILURE-TAXONOMY); shown honestly,
        // never silently absorbed into "refused".
        return (
          <li
            key={outcome.objectSid}
            className="ra-outcome ra-outcome--failed"
            data-testid={`ra-outcome-${outcome.objectSid}`}
            role="alert"
          >
            <strong>{label}</strong> could not be changed: {outcome.message}
            {' '}(<span data-testid={`ra-outcome-reason-${outcome.objectSid}`}>{outcome.failureReason}</span>).
          </li>
        );
      })}
    </ul>
    {unresolved && doorPoll?.status === 'expired' ? (
      <p className="ra-outcome-door-expired" data-testid="ra-door-unreported" role="status">
        {DOOR_UNREPORTED_TEXT}
        {' '}
        <button type="button" className="btn btn-outline" onClick={() => doorPoll.checkAgain()} disabled={doorPoll.checking} data-testid="ra-door-check-again">
          {doorPoll.checking ? 'Checking…' : 'Check again'}
        </button>
        {doorPoll.checkFailed ? (
          <span data-testid="ra-door-check-failed">{' '}{DOOR_CHECK_FAILED_TEXT}</span>
        ) : null}
      </p>
    ) : null}
    </>
  );
}
